/**
 * Translation-key blueprint for the six primary wheel tools.
 *
 * The translated strings live in public/locales/<lang>.json so the browser
 * still downloads only the active language. This module contains structure,
 * not translations, and is shared by React and the static prerenderer so the
 * visible FAQ/HowTo copy and JSON-LD are built from the same source.
 */

export const MAIN_TOOL_ROUTES = [
  '/',
  '/random-team-generator',
  '/classroom-name-picker',
  '/giveaway-wheel',
  '/wheel-of-fortune-maker',
  '/yes-no-wheel',
] as const;

export type MainToolRoute = typeof MAIN_TOOL_ROUTES[number];
export type MainToolId = 'home' | 'teams' | 'classroom' | 'giveaway' | 'fortune' | 'yesno';

export const MAIN_TOOL_ID_BY_ROUTE: Record<MainToolRoute, MainToolId> = {
  '/': 'home',
  '/random-team-generator': 'teams',
  '/classroom-name-picker': 'classroom',
  '/giveaway-wheel': 'giveaway',
  '/wheel-of-fortune-maker': 'fortune',
  '/yes-no-wheel': 'yesno',
};

export interface MainToolFaq {
  q: string;
  a: string;
}

export interface MainToolContent {
  id: MainToolId;
  h1: string;
  intro: string;
  howTitle: string;
  steps: string[];
  exampleTitle: string;
  example: string;
  featuresTitle: string;
  features: string;
  limitationsTitle: string;
  limitations: string;
  faqTitle: string;
  faqs: MainToolFaq[];
}

export type Translate = (key: string, vars?: Record<string, string | number>) => string;
export type HasTranslation = (key: string) => boolean;

export function mainToolIdForRoute(route: string): MainToolId | null {
  const normalized = route !== '/' ? route.replace(/\/$/, '') : route;
  return (MAIN_TOOL_ID_BY_ROUTE as Record<string, MainToolId>)[normalized] ?? null;
}

export function mainToolKey(id: MainToolId, field: string): string {
  return `tool.${id}.${field}`;
}

/** Keep the editable wheel label concise without changing the SEO-focused H1. */
export function mainToolWheelTitle(h1: string): string {
  return h1.split(/\s+[\u2014\u2013-]\s+|:\s+/u, 1)[0].trim();
}

/** Confirm that the active dictionary contains the complete concise tool set. */
export function hasMainToolTranslations(id: MainToolId, has: HasTranslation): boolean {
  return [
    'tool.content.howTitle',
    'tool.content.exampleTitle',
    'tool.content.featuresTitle',
    'tool.content.limitationsTitle',
    'tool.content.faqTitle',
    ...[
      'h1', 'intro', 'step1', 'step2', 'step3', 'example', 'features',
      'limitations', 'faq1q', 'faq1a', 'faq2q', 'faq2a',
    ].map(field => mainToolKey(id, field)),
  ].every(has);
}

/** Resolve one tool's translated, visible content from the active dictionary. */
export function resolveMainToolContent(id: MainToolId, t: Translate): MainToolContent {
  const key = (field: string) => t(mainToolKey(id, field));
  return {
    id,
    h1: key('h1'),
    intro: key('intro'),
    howTitle: t('tool.content.howTitle'),
    steps: [key('step1'), key('step2'), key('step3')],
    exampleTitle: t('tool.content.exampleTitle'),
    example: key('example'),
    featuresTitle: t('tool.content.featuresTitle'),
    features: key('features'),
    limitationsTitle: t('tool.content.limitationsTitle'),
    limitations: key('limitations'),
    faqTitle: t('tool.content.faqTitle'),
    faqs: [
      { q: key('faq1q'), a: key('faq1a') },
      { q: key('faq2q'), a: key('faq2a') },
    ],
  };
}

/**
 * Build translated content directly from a prerender dictionary.
 * Returns null unless every required string is a real translation, preventing
 * mixed-language schema or a localized page that silently contains key names.
 */
export function mainToolContentFromDict(
  route: string,
  dict: Record<string, string> | undefined,
  isRealTranslation: (value: unknown) => value is string,
): MainToolContent | null {
  const id = mainToolIdForRoute(route);
  if (!id || !dict) return null;

  const required = [
    'tool.content.howTitle',
    'tool.content.exampleTitle',
    'tool.content.featuresTitle',
    'tool.content.limitationsTitle',
    'tool.content.faqTitle',
    ...[
      'h1', 'intro', 'step1', 'step2', 'step3', 'example', 'features',
      'limitations', 'faq1q', 'faq1a', 'faq2q', 'faq2a',
    ].map(field => mainToolKey(id, field)),
  ];
  if (!required.every(key => isRealTranslation(dict[key]))) return null;

  const translate: Translate = key => dict[key];
  return resolveMainToolContent(id, translate);
}

export function mainToolHowTo(content: MainToolContent) {
  return {
    name: content.howTitle,
    description: content.intro,
    tool: 'TheNameWheel',
    steps: content.steps.map((step, index) => ({
      title: `${index + 1}`,
      desc: step,
    })),
  };
}
